import React, { useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { OrgInitialsAvatar } from "./OrgInitialsAvatar";

export interface OrgHeaderButtonProps {
	name: string;
	letter: string;
	isExpanded: boolean;
	isPinned: boolean;
	isMenuOpen: boolean;
	setIsMenuOpen: (isMenuOpen: boolean) => void;
	children?: React.ReactNode;
}

/**
 * Organization header button with expandable menu
 * Shows organization avatar and name (when expanded)
 */
export const OrgHeaderButton: React.FC<OrgHeaderButtonProps> = ({
	name,
	letter,
	isExpanded,
	isPinned,
	isMenuOpen,
	setIsMenuOpen,
	children,
}) => {

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	// Close menu when sidebar collapses
	useEffect(() => {
		if (!isExpanded && !isPinned && isMenuOpen) {
			setIsMenuOpen(false);
		}
	}, [isExpanded, isPinned, isMenuOpen, setIsMenuOpen]);

	return (
		<>

			<div className="relative w-full">
				{/* Header Button */}
				<div
					className="w-full flex items-center gap-2 hover:cursor-pointer hover:bg-stone-200 rounded-md py-1"
					onClick={toggleMenu}
				>
					{/* Organization avatar - matching client avatar size and centered when collapsed */}
					<div
						className={
							isExpanded || isPinned
								? "pl-2"
								: "flex justify-center w-full"
						}
					>
						<OrgInitialsAvatar letter={letter} size={20} />
					</div>

					{/* Organization name and chevron - only show when expanded */}
					{(isPinned || isExpanded) && (
						<div className="flex items-center justify-start pr-2 gap-2">
							<div className="text-sm font-medium text-gray-700 truncate flex-1 max-w-[105px]">
								{name}
							</div>
							{isMenuOpen ? (
								<ChevronUp
									size={16}
									className="text-gray-700 flex-shrink-0"
								/>
							) : (
								<ChevronDown
									size={16}
									className="text-gray-700 flex-shrink-0"
								/>
							)}
						</div>
					)}
				</div>

				{/* Extended Menu Content */}
				{isMenuOpen && (isPinned || isExpanded) && (
					<div className="absolute top-full left-0 w-56 mt-1 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
						{children}
					</div>
				)}
			</div>
		</>
	);
};

interface OrgHeaderButtonMenuItemProps {
	icon: React.ReactNode;
	label: string;
	onClick: () => void;
}

export const OrgHeaderButtonMenuItem: React.FC<OrgHeaderButtonMenuItemProps> = ({
	icon,
	label,
	onClick,
}) => {
	return (
		<button
			className="w-full flex items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
			onClick={onClick}
		>
			<div className="mr-2">
				{icon}
			</div>
			<span>{label}</span>
		</button>
	);
};
